import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Building2, Plus, Trash2, MapPin, Home } from 'lucide-react';
import { toast } from 'sonner';
import errMsg from '../utils/apiError';

const TYPES = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'immeuble', label: 'Immeuble' },
  { value: 'lmnp', label: 'LMNP' },
  { value: 'lmp', label: 'LMP' },
  { value: 'parking', label: 'Parking' },
  { value: 'local_commercial', label: 'Local commercial' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'sci', label: 'SCI' },
  { value: 'autre', label: 'Autre' },
];
const STATUS = [
  { value: 'recherche', label: 'En recherche' },
  { value: 'signe', label: 'Signé' },
  { value: 'loue', label: 'Loué' },
  { value: 'revendu', label: 'Revendu' },
];
const MAX = 10;

export default function InvestmentsManager({ api }) {
  const [items, setItems] = useState([]);
  const [showPublic, setShowPublic] = useState(true);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ city: '', type: 'appartement', status: 'recherche', notes: '' });

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const fetchData = async () => {
    try {
      const r = await api.get('/members/me/investments');
      setItems(r.data?.investments || []);
      setShowPublic(r.data?.show_investments !== false);
    } catch (e) {
      toast.error(errMsg(e, 'Erreur chargement investissements'));
    } finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.city.trim()) { toast.error('Ville requise'); return; }
    setAdding(true);
    try {
      const r = await api.post('/members/me/investments', form);
      setItems((prev) => [...prev, r.data.investment]);
      setForm({ city: '', type: 'appartement', status: 'recherche', notes: '' });
      toast.success('🏠 Investissement ajouté à ton profil');
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Retirer cet investissement de ton profil ?')) return;
    try {
      await api.delete(`/members/me/investments/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success('Retiré');
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleVisibility = async (value) => {
    setShowPublic(value);
    try {
      await api.put(`/members/me/investments-visibility?show=${value}`);
    } catch (e) {
      toast.error(errMsg(e));
      setShowPublic(!value);
    }
  };

  return (
    <Card className="card-base" data-testid="investments-manager">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary" />
          Mes investissements
        </CardTitle>
        <CardDescription>
          Ajoute jusqu'à {MAX} biens. Sur la carte communautaire, les pins restent <strong>anonymes</strong> (ville + type uniquement). Sur ton propre profil, tu choisis ce qui est visible.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Public toggle */}
        <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
          <div>
            <p className="font-medium">Afficher sur mon profil public</p>
            <p className="text-sm text-muted-foreground">Les autres membres verront la liste dans l'onglet Investissements</p>
          </div>
          <Switch checked={showPublic} onCheckedChange={toggleVisibility} data-testid="show-invest-switch" />
        </div>

        {/* List */}
        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 p-3 border border-border rounded-lg bg-card">
                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Home className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{TYPES.find(t=>t.value===inv.type)?.label || inv.type}</span>
                    <span className="text-muted-foreground text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {inv.city}
                    </span>
                    <Badge variant="outline" className="text-[10px]">
                      {STATUS.find(s=>s.value===inv.status)?.label || inv.status}
                    </Badge>
                  </div>
                  {inv.notes && <p className="text-xs text-muted-foreground truncate mt-0.5">{inv.notes}</p>}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(inv.id)}
                  className="h-8 w-8 text-red-500 hover:text-red-600"
                  data-testid={`delete-inv-${inv.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add form */}
        {items.length < MAX ? (
          <form onSubmit={handleAdd} className="grid sm:grid-cols-4 gap-2 items-end bg-secondary/20 p-3 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="inv-city" className="text-xs">Ville *</Label>
              <Input
                id="inv-city"
                placeholder="Lille"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="inv-city-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv-type" className="text-xs">Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger id="inv-type" data-testid="inv-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv-status" className="text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger id="inv-status" data-testid="inv-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={adding} data-testid="add-inv-btn">
              <Plus className="w-4 h-4 mr-1" /> {adding ? '…' : 'Ajouter'}
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2">
            Limite de {MAX} investissements atteinte.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
